import type { DashboardSharingType } from '@risksmart-app/domain/src/types/consts/dashboard-sharing-type';
import type {
  InputMaybe,
  InsertDashboardMutation,
  Scalars,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface InsertDashboardTRPCInput {
  Name: string;
  Description?: InputMaybe<string>;
  Sharing: DashboardSharingType;
  Content?: InputMaybe<Scalars['jsonb']['input']>;
  ContributorUserIds: string[];
  ContributorGroupIds: string[];
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertDashboardMutation => {
  return {
    insertChildDashboard: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertDashboardTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.dashboard.insert.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.dashboard.dashboards.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.dashboard.dashboardById.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertDashboard: async (
      variables: InsertDashboardTRPCInput
    ): Promise<InsertDashboardMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
